import { Box, Typography, Chip, Paper, Select, MenuItem, IconButton } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { Close, CallMerge } from "@mui/icons-material";
import { Droppable } from "@hello-pangea/dnd";
import type { GrowerInfo, GrowerSortOption, JobCardInfo } from "./types";
import { GrowerCard } from "./GrowerCard";
import { JobCard } from "./JobCard";
import { makeGrowerDraggableId } from "./draggableId.helpers";
import { PortalTooltip } from "../../../../../../helpers";

export function UnassignedGrowersPanel({
	unassignedGrowers,
	unassignedJobs = [],
	growerSortOption,
	setGrowerSortOption,
	selectedItems,
	clearSelection,
	handleItemClick,
	draggingSelectedCount,
	UNASSIGNED_DROPPABLE_ID,
	onExplodeGrower,
	onRegroupGrower,
}: {
	unassignedGrowers: GrowerInfo[];
	unassignedJobs?: JobCardInfo[];
	growerSortOption: GrowerSortOption;
	setGrowerSortOption: (option: GrowerSortOption) => void;
	selectedItems: Set<string>;
	clearSelection: () => void;
	handleItemClick: (draggableId: string, shiftKey: boolean) => void;
	draggingSelectedCount: number;
	UNASSIGNED_DROPPABLE_ID: string;
	onExplodeGrower?: (growerName: string) => void;
	onRegroupGrower?: (growerName: string) => void;
}) {
	// Group unassigned jobs by grower for rendering with subheaders
	const unassignedJobsByGrower = new Map<string, JobCardInfo[]>();
	for (const job of unassignedJobs) {
		const existing = unassignedJobsByGrower.get(job.growerName);
		if (existing) {
			existing.push(job);
		} else {
			unassignedJobsByGrower.set(job.growerName, [job]);
		}
	}

	// Get exploded grower names that have unassigned jobs
	const explodedGrowerNames = Array.from(unassignedJobsByGrower.keys()).sort();

	const totalUnassigned = unassignedGrowers.length + unassignedJobs.length;

	// Build a continuous index for all draggable items
	let itemIndex = 0;

	return (
		<Box sx={{ width: 260, flexShrink: 0, display: "flex", flexDirection: "column" }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
				<Typography variant="subtitle2" sx={{ whiteSpace: "nowrap" }}>
					Unassigned ({totalUnassigned})
				</Typography>
				<Select
					size="small"
					value={growerSortOption}
					onChange={(e: SelectChangeEvent) => setGrowerSortOption(e.target.value as GrowerSortOption)}
					sx={{
						fontSize: "0.75rem",
						height: 28,
						minWidth: 0,
						ml: "auto",
						"& .MuiSelect-select": { py: 0.25, pr: "24px !important", pl: 1 },
					}}
				>
					<MenuItem value="name-asc" sx={{ fontSize: "0.8rem" }}>
						Name A-Z
					</MenuItem>
					<MenuItem value="name-desc" sx={{ fontSize: "0.8rem" }}>
						Name Z-A
					</MenuItem>
					<MenuItem value="count-desc" sx={{ fontSize: "0.8rem" }}>
						Jobs High→Low
					</MenuItem>
					<MenuItem value="count-asc" sx={{ fontSize: "0.8rem" }}>
						Jobs Low→High
					</MenuItem>
				</Select>
				{selectedItems.size > 0 && (
					<Chip
						label={`${selectedItems.size} selected`}
						size="small"
						color="primary"
						onDelete={clearSelection}
						deleteIcon={<Close fontSize="small" />}
						sx={{ flexShrink: 0 }}
					/>
				)}
			</Box>
			<Droppable droppableId={UNASSIGNED_DROPPABLE_ID}>
				{(provided, snapshot) => (
					<Paper
						ref={provided.innerRef}
						{...provided.droppableProps}
						variant="outlined"
						sx={{
							flex: 1,
							p: 1,
							overflowY: "auto",
							bgcolor: snapshot.isDraggingOver ? "action.hover" : "grey.50",
							minHeight: 100,
						}}
					>
						{totalUnassigned === 0 && (
							<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 2 }}>
								All jobs assigned
							</Typography>
						)}

						{/* Collapsed grower cards */}
						{unassignedGrowers.map((grower) => {
							const idx = itemIndex++;
							return (
								<GrowerCard
									key={makeGrowerDraggableId(grower.name)}
									grower={grower}
									index={idx}
									isSelected={selectedItems.has(makeGrowerDraggableId(grower.name))}
									onToggleSelect={handleItemClick}
									multiDragCount={selectedItems.has(makeGrowerDraggableId(grower.name)) ? draggingSelectedCount : 0}
									onExplode={onExplodeGrower}
								/>
							);
						})}

						{/* Exploded grower sections with individual job cards */}
						{explodedGrowerNames.map((growerName) => {
							const jobs = unassignedJobsByGrower.get(growerName) || [];
							return (
								<Box key={`exploded-${growerName}`} sx={{ mb: 1 }}>
									{/* Non-draggable group header */}
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											py: 0.5,
											px: 0.5,
											borderBottom: "1px solid",
											borderColor: "divider",
											mb: 0.5,
										}}
									>
										<Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>
											{growerName} ({jobs.length} jobs)
										</Typography>
										{onRegroupGrower && (
											<PortalTooltip title="Re-group into grower card">
												<IconButton size="small" onClick={() => onRegroupGrower(growerName)} sx={{ p: 0.25 }}>
													<CallMerge fontSize="small" />
												</IconButton>
											</PortalTooltip>
										)}
									</Box>
									{jobs.map((job) => {
										const idx = itemIndex++;
										return (
											<JobCard
												key={`job:${job.jobId}`}
												job={job}
												index={idx}
												isSelected={selectedItems.has(`job:${job.jobId}`)}
												onToggleSelect={handleItemClick}
												multiDragCount={selectedItems.has(`job:${job.jobId}`) ? draggingSelectedCount : 0}
											/>
										);
									})}
								</Box>
							);
						})}

						{provided.placeholder}
					</Paper>
				)}
			</Droppable>
		</Box>
	);
}
